import React, { useState } from "react";
import { Box, Typography, Drawer, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WidgetsRoundedIcon from "@mui/icons-material/WidgetsRounded";
import LocalMallRoundedIcon from "@mui/icons-material/LocalMallRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import SearchIcon from "@mui/icons-material/Search";
import ColorPicker from "./ColorPicker";
import LogoMenu from "./LogoMenu";

export const ButtonItem = ({ content = {}, design = {}, height = 50 }) => {
  return (
    <>
      {content?.image && content?.imageEnable ? (
        <img
          src={content.image}
          alt={content?.buttonText ?? "Button Image"}
          style={{
            width: height < 41 ? "1.4rem" : height < 47 ? "1.6rem" : "2rem",
            height: height < 41 ? "1.4rem" : height < 47 ? "1.6rem" : "2rem",
            borderRadius: "9999px",
            objectFit: "cover",
            objectPosition: "center",
          }}
        />
      ) : (
        <Box
          sx={{
            backgroundColor: "#7f8b8a",
            width: height < 41 ? "1.4rem" : height < 47 ? "1.6rem" : "2rem",
            height: height < 41 ? "1.4rem" : height < 47 ? "1.6rem" : "2rem",
            borderRadius: "9999px",
          }}
        />
      )}
      <Typography
        variant="h6"
        sx={{
          fontSize: height < 41 ? "14px" : height < 47 ? "15px" : "16px",
          textTransform: "capitalize",
          fontWeight: content?.buttonTextType ?? "normal",
          background: design?.textColor ?? "#000",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          color: "transparent",
          transition: "all 0.3s ease",
        }}
      >
        {content?.buttonText ?? "Button"}
      </Typography>
    </>
  );
};

export const HeaderItem = ({
  toggleDrawer,
  index,
  design = {},
  content = {},
  action = {},
  height = 50,
}) => {
  return (
    <Box
      sx={{
        border: `1px solid transparent`,
        background: design?.borderColor ?? "transparent",
        borderRadius: "50px",
        cursor: "pointer",
        transition: "all 0.3s ease",
        opacity: content?.isActive ? 1 : 0.5,

        // `&:active` styles for parent Box
        "&:active": {
          background: `${design.borderActiveColor} !important`,

          // Active effect for child Typography
          "& .MuiTypography-root": {
            background: `${design.textActiveColor} !important`,
            backgroundClip: "text !important",
            WebkitBackgroundClip: "text !important",
            color: "transparent",
          },

          // Active effect for child Box
          "& .inner-box": {
            background: `${design.buttonActiveColor} !important`,
          },
        },

        "&:hover": {
          background: design.borderHoverColor,

          // Adding hover effect for child Typography when parent is hovered
          "& .MuiTypography-root": {
            background: design.textHoverColor,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
          },

          // Child inner Box hover effect (image or placeholder box)
          "& .inner-box": {
            background: design.buttonHoverColor,
          },
        },
      }}
    >
      <Box
        className="inner-box"
        sx={{
          display: "flex",
          alignItems: "center",
          columnGap: "10px",
          padding: height < 41 ? "2px 3px" : "4px 5px",
          borderRadius: "50px",
          background: design?.buttonColor ?? "#ddd",
          transition: "all 0.3s ease",
        }}
      >
        {action?.isClickAble && action?.url ? (
          <a
            href={action.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              columnGap: "10px",
            }}
          >
            <ButtonItem content={content} design={design} height={height} />
          </a>
        ) : (
          <ButtonItem content={content} design={design} height={height} />
        )}
        <Box
          sx={{
            backgroundColor: "#fff",
            width: height < 41 ? "1.4rem" : height < 47 ? "1.6rem" : "2rem",
            height: height < 41 ? "1.4rem" : height < 47 ? "1.6rem" : "2rem",
            borderRadius: "9999px",
            color: "#000",
            display: "grid",
            placeItems: "center",
            cursor: "pointer",
          }}
          onClick={() => toggleDrawer?.(true, index, "menu")}
        >
          <EditRoundedIcon fontSize="small" />
        </Box>
      </Box>
    </Box>
  );
};

export const EditHeaderBG = ({
  itemText,
  toggleDrawer,
  index,
  isMobile,
  height,
}) => {
  let padding;
  if (index !== 1) {
    if (height < 41) {
      padding = "3px 0";
    } else if (height < 47) {
      padding = isMobile ? "4px 0" : "6px 0";
    } else {
      padding = isMobile ? "4px 0" : "8px 0";
    }
  } else {
    if (!isMobile) {
      if (height < 93) {
        padding = "3px 0";
      } else {
        padding = "8px 0";
      }
    } else {
      if (height < 65) {
        padding = "1px 0";
      } else {
        padding = "4px 0";
      }
    }
  }
  let styling = {
    backgroundColor: "#7f7d7d",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: padding,
    width: "177px",
    borderRadius: "50px",
    cursor: "pointer",
  };

  if (isMobile) {
    styling["width"] = "100px";
    // styling["padding"] = "2px 0";
  }
  return (
    <Box
      sx={styling}
      onClick={() => {
        toggleDrawer(true, index, "test");
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: "normal",
          fontSize: isMobile
            ? "13px"
            : index !== 1
              ? height < 46
                ? "14px"
                : "16px"
              : height < 73
                ? "14px"
                : "16px",
        }}
      >
        {itemText}
      </Typography>
    </Box>
  );
};

export const EditHeaderLogo = ({ toggleDrawer, isMobile, height }) => {
  let styling = {
    backgroundColor: "#7f7d7d",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: isMobile
      ? height < 65
        ? "1px 0"
        : "4px 0"
      : height < 93
        ? "3px 0"
        : "8px 0",
    width: "177px",
    borderRadius: "50px",
    cursor: "pointer",
  };

  if (isMobile) {
    styling["width"] = "100px";
    // styling["padding"] = "2px 0";
  }
  return (
    <Box
      sx={styling}
      onClick={() => {
        toggleDrawer(true, null, "customizeHeader", "customizeHeader");
      }}
    >
      <Typography
        variant="h6"
        sx={{ fontWeight: "normal", fontSize: height < 73 ? "13px" : "16px" }}
      >
        Edit Header
      </Typography>
    </Box>
  );
};

const Header = ({
  toggleDrawer,
  headerItemData = {},
  setHeaderItemData,
  editItem = {},
  handleSave,
  handleApi,
  setIsMobile,
  isMobile,
}) => {
  const [searchBarExpand, setSearchBarExpand] = useState(true);

  const {
    customHeader = {},
    customTopbar = {},
    customNavbar = {},
  } = headerItemData?.customHeader || {};
  const { height: customHeaderHeight = 100, customizeHeader = {} } =
    customHeader;
  const { search: searchStyles = {}, items: itemStyles = {} } = customizeHeader;
  const { shadow: searchShadow = {} } = searchStyles;
  const { shadow: itemShadow = {} } = itemStyles;

  // Convert hex color to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
      : null;
  };

  const searchrgb = hexToRgb(searchShadow?.color);
  const searchopacity = (searchShadow?.opacity ?? 0) / 100;
  const searchrgba = searchrgb
    ? `rgba(${searchrgb.r}, ${searchrgb.g}, ${searchrgb.b}, ${searchopacity})`
    : `rgba(0, 0, 0, ${searchopacity})`;

  const itemsrgb = hexToRgb(itemShadow?.color);
  const itemsopacity = (itemShadow?.opacity ?? 0) / 100;
  const itemsrgba = itemsrgb
    ? `rgba(${itemsrgb.r}, ${itemsrgb.g}, ${itemsrgb.b}, ${itemsopacity})`
    : `rgba(0, 0, 0, ${itemsopacity})`;
  return (
    <Box
      sx={{
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "200px",
        zIndex: 1000,
      }}
    >
      <Drawer
        open={editItem.open && editItem.item === "bgColor"}
        onClose={() => toggleDrawer(false)}
        anchor="right"
        PaperProps={{
          sx: {
            height: "auto",
            width: "370px",
            paddingBottom: "20px",
            overflow: "hidden",
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        {editItem.open && editItem.item === "bgColor" && (
          <ColorPicker
            toggleDrawer={toggleDrawer}
            editItem={editItem}
            handleSave={handleSave}
            initialColor={customTopbar?.links[editItem?.index][editItem?.item]}
          />
        )}
      </Drawer>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          height: `${customTopbar?.height ?? 60}px`,
          borderBottom: "3px solid #7f7d7d",
          padding: "0 5px",
          background: customTopbar?.backgroundColor ?? "#242424",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flex: 1,
            paddingRight: "5px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              columnGap: "10px",
            }}
          >
            <HeaderItem
              toggleDrawer={toggleDrawer}
              index={0}
              design={customTopbar?.links?.[0].design}
              content={customTopbar?.links?.[0].content}
              action={customTopbar?.links?.[0].action}
              height={customTopbar?.height}
            />
            <HeaderItem
              toggleDrawer={toggleDrawer}
              index={1}
              design={customTopbar?.links?.[1].design}
              content={customTopbar?.links?.[1].content}
              action={customTopbar?.links?.[1].action}
              height={customTopbar?.height}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              columnGap: "10px",
            }}
          >
            <IconButton
              onClick={() => {
                toggleDrawer(false);
                setIsMobile(true);
              }}
              sx={{
                p: 0,
                "&.MuiButtonBase-root": {
                  padding: 0,
                },
                width: 40,
                height: 40,
                borderRadius: "50%",
                color: "#333",
                backgroundColor: "#ffffff",
                "&:hover": {
                  backgroundColor: "#ffffff",
                },
              }}
            >
              <PhoneAndroidIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              columnGap: "10px",
            }}
          >
            <HeaderItem
              toggleDrawer={toggleDrawer}
              index={2}
              design={customTopbar?.links?.[2].design}
              content={customTopbar?.links?.[2].content}
              action={customTopbar?.links?.[2].action}
              height={customTopbar?.height}
            />
            <HeaderItem
              toggleDrawer={toggleDrawer}
              index={3}
              design={customTopbar?.links?.[3].design}
              content={customTopbar?.links?.[3].content}
              action={customTopbar?.links?.[3].action}
              height={customTopbar?.height}
            />
          </Box>
        </Box>
        <Box
          sx={{
            borderLeft: "3px solid #7f7d7d",
            height: "100%",
            display: "flex",
            alignItems: "center",
            padding: "0 5px",
          }}
        >
          <EditHeaderBG
            itemText="Edit Header 1"
            toggleDrawer={toggleDrawer}
            index={0}
            isMobile={isMobile}
            height={customTopbar.height}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          height: `${customHeaderHeight ?? 100}px`,
          borderBottom: "3px solid #7f7d7d",
          padding: "0 5px",
          background:
            headerItemData?.customHeader?.customHeader?.backgroundColor ??
            "#242424",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flex: 1,
            paddingRight: "5px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              columnGap: "10px",
              flex: 0.3,
            }}
          >
            <Box
              sx={{
                border: "1px solid transparent",
                borderRadius: "50px",
                background:
                  headerItemData?.customHeader?.customHeader?.customizeHeader
                    ?.search?.borderColor,
                backgroundOrigin: "border-box",
              }}
            >
              <Box
                sx={{
                  background:
                    headerItemData?.customHeader?.customHeader?.customizeHeader
                      ?.search?.backgroundColor ?? "#fff",
                  boxShadow: `${searchShadow.inset ? "inset " : ""}${searchShadow.horizontal
                    }px ${searchShadow.vertical}px ${searchShadow.blur}px ${searchShadow.spread
                    }px ${searchrgba}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50px",
                  height: customHeaderHeight > 130 ? "40px" : "35px",
                  width: searchBarExpand
                    ? "14rem"
                    : customHeaderHeight > 130
                      ? "40px"
                      : "35px",
                  transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                {searchBarExpand && (
                  <input
                    className="search-placeholder"
                    placeholder="Search"
                    style={{
                      outline: "none",
                      border: "none",
                      backgroundColor: "transparent",
                      width: "83%",
                      color:
                        headerItemData?.customHeader?.customHeader
                          ?.customizeHeader?.search?.textColor ?? "#333",
                    }}
                  />
                )}
                {!searchBarExpand && (
                  <SearchIcon
                    sx={{
                      color:
                        headerItemData.customHeader.customHeader.customizeHeader
                          .search.textColor,
                      cursor: "pointer",
                    }}
                    onClick={() => setSearchBarExpand(true)}
                  />
                )}
              </Box>
            </Box>
            {searchBarExpand && (
              <Box
                sx={{
                  border: "1px solid transparent",
                  borderRadius: "50px",
                  background:
                    headerItemData?.customHeader?.customHeader?.customizeHeader
                      ?.search?.borderColor ?? "#333",
                  backgroundOrigin: "border-box",
                }}
              >
                <Box
                  style={{
                    height: customHeaderHeight > 130 ? "40px" : "35px",
                    width: customHeaderHeight > 130 ? "40px" : "35px",
                    background:
                      headerItemData?.customHeader?.customHeader
                        ?.customizeHeader?.search?.backgroundColor ?? "#fff",
                    boxShadow: `${searchShadow.inset ? "inset " : ""}${searchShadow.horizontal
                      }px ${searchShadow.vertical}px ${searchShadow.blur}px ${searchShadow.spread
                      }px ${searchrgba}`,
                    borderRadius: "50px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CloseIcon
                    sx={{
                      color:
                        headerItemData?.customHeader?.customHeader
                          ?.customizeHeader?.search?.textColor ?? "#333",
                      cursor: "pointer",
                    }}
                    onClick={() => setSearchBarExpand(false)}
                  />
                </Box>
              </Box>
            )}
          </Box>

          {/* Add SUNDIAL here */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 20px",
              flex: 0.4,
            }}
          >
            {headerItemData.customHeader?.customHeader?.logo?.logo ? (
              <img
                src={headerItemData.customHeader?.customHeader?.logo?.logo}
                alt="Header Logo"
                style={{
                  height:
                    (headerItemData.customHeader?.customHeader?.logo?.height ||
                      "75") + "px",
                  width:
                    (headerItemData.customHeader?.customHeader?.logo?.width ||
                      "75") + "px",
                }}
              />
            ) : (
              <Typography
                variant="h4"
                sx={{
                  fontSize: customHeaderHeight > 130 ? "50px" : "45px",
                  letterSpacing: "0.4em",
                  color: "#fff",
                }}
              >
                SUNDIAL
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              columnGap: "10px",
              flex: 0.3,
              justifyContent: "flex-end",
            }}
          >
            {/* Cart and Menu */}
            <Box
              sx={{
                border: "1px solid transparent",
                background:
                  headerItemData?.customHeader?.customHeader?.customizeHeader
                    ?.items?.borderColor ?? "#333",
                backgroundOrigin: "border-box",
                boxShadow: `${itemShadow.inset ? "inset " : ""}${itemShadow.horizontal
                  }px ${itemShadow.vertical}px ${itemShadow.blur}px ${itemShadow.spread
                  }px ${itemsrgba}`,
                borderRadius: "50px",
                cursor: "pointer",
              }}
            >
              <Box
                sx={{
                  background:
                    headerItemData?.customHeader?.customHeader?.customizeHeader
                      ?.items?.backgroundColor ?? "#fff",
                  display: "flex",
                  alignItems: "center",
                  columnGap: "10px",
                  padding: customHeaderHeight > 130 ? "8px 7px" : "6px 5px",
                  borderRadius: "50px",
                }}
              >
                <WidgetsRoundedIcon
                  fontSize="small"
                  sx={{
                    color:
                      headerItemData?.customHeader?.customHeader
                        ?.customizeHeader?.items?.textColor ?? "#333",
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "normal",
                    fontSize: "16px",
                    color:
                      headerItemData?.customHeader?.customHeader
                        ?.customizeHeader?.items?.textColor ?? "#333",
                  }}
                >
                  Menu
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                border: "1px solid transparent",
                background:
                  headerItemData?.customHeader?.customHeader?.customizeHeader
                    ?.items?.borderColor ?? "#333",
                backgroundOrigin: "border-box",
                boxShadow: `${itemShadow.inset ? "inset " : ""}${itemShadow.horizontal
                  }px ${itemShadow.vertical}px ${itemShadow.blur}px ${itemShadow.spread
                  }px ${itemsrgba}`,
                borderRadius: "50px",
                cursor: "pointer",
              }}
            >
              <Box
                sx={{
                  background:
                    headerItemData?.customHeader?.customHeader?.customizeHeader
                      ?.items.backgroundColor ?? "#fff",
                  display: "flex",
                  alignItems: "center",
                  columnGap: "10px",
                  padding: customHeaderHeight > 130 ? "8px 7px" : "6px 5px",
                  borderRadius: "50px",
                }}
              >
                <LocalMallRoundedIcon
                  fontSize="small"
                  sx={{
                    color:
                      headerItemData?.customHeader?.customHeader
                        ?.customizeHeader?.items?.textColor ?? "#333",
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "normal",
                    fontSize: "16px",
                    color:
                      headerItemData?.customHeader?.customHeader
                        ?.customizeHeader?.items?.textColor ?? "#333",
                  }}
                >
                  Cart
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            borderLeft: "3px solid #7f7d7d",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            rowGap: "5px",
            padding: "0 5px",
          }}
        >
          <EditHeaderBG
            itemText="Edit Header 2"
            toggleDrawer={toggleDrawer}
            index={1}
            isMobile={isMobile}
            height={headerItemData?.customHeader?.customHeader?.height}
          />
          <EditHeaderLogo
            toggleDrawer={toggleDrawer}
            isMobile={isMobile}
            height={headerItemData?.customHeader?.customHeader?.height}
          />

          <Drawer
            open={
              editItem.open &&
              editItem.item === "logo" &&
              editItem.index === null
            }
            onClose={() => toggleDrawer(false)}
            anchor="right"
            PaperProps={{
              sx: {
                height: "auto",
                width: "370px",
                paddingBottom: "20px",
                overflow: "hidden",
                backgroundColor: "transparent",
                boxShadow: "none",
              },
            }}
          >
            {editItem.open && editItem.item === "logo" && (
              <LogoMenu
                toggleDrawer={toggleDrawer}
                editItem={editItem}
                handleSave={handleSave}
                logo={headerItemData?.customHeader?.customHeader?.logo}
                handleApi={handleApi}
                isMobile={isMobile}
                height={headerItemData?.customHeader?.customHeader?.height}
              />
            )}
          </Drawer>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "end",
          width: "100%",
          height: `${customNavbar?.height ?? 60}px`,
          padding: "0 5px",
          background: customNavbar?.backgroundColor ?? "#242424",
        }}
      >
        <Box
          sx={{
            borderLeft: "3px solid #7f7d7d",
            height: "100%",
            display: "flex",
            alignItems: "center",
            padding: "0 5px",
          }}
        >
          <EditHeaderBG
            itemText="Edit Header 3"
            toggleDrawer={toggleDrawer}
            index={2}
            isMobile={isMobile}
            height={customNavbar?.height}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Header;
